import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { BROWSE_SEARCH_PAGE_FACET_FIELD_IDS, FILTERABLE_DISPLAY_BIB_GRID_LIMIT } from 'app/constants/BrowseConstants';
import { deserializeQuerystring, serializeQuerystring } from 'app/helpers/browse/searchPageQueryHelpers';
import { makeBackToLinkProps } from 'app/helpers/browse/search';
import useBrowseFacets from 'app/hooks/useBrowseFacets';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import FilterableDisplayBibGrid from '../shared/FilterableDisplayBibGrid';
import BrowseSearchPage from './BrowseSearchPage';

const QUERY_STRING_KEY = {
  BACK_TO_PATH: 'returnToPath',
  BACK_TO_TITLE: 'returnToTitle',
  OVERRIDE_HEADING: 'title'
};

const BrowseSearchPageContainer = () => {
  const intl = useIntl();
  const { search } = useLocation();
  const currentLibrary = useSelector(selectCurrentLibrary);

  const urlSearchParams = new URLSearchParams(search);
  const {
    query,
    searchType,
    [QUERY_STRING_KEY.BACK_TO_PATH]: backToCmsPath,
    [QUERY_STRING_KEY.BACK_TO_TITLE]: backToCmsTitle,
    [QUERY_STRING_KEY.OVERRIDE_HEADING]: overrideHeading
  } = Object.fromEntries(urlSearchParams);
  const baseQuery = { query, searchType };

  const baseApiParams = { limit: FILTERABLE_DISPLAY_BIB_GRID_LIMIT, ...baseQuery };

  const {
    displayBibs,
    facetFields,
    handleFilterChange,
    isFetching,
    pagination,
    setShowMoreFieldId,
    unapplyAllFilters
  } = useBrowseFacets({
    baseApiParams,
    baseQuerystring: new URLSearchParams(baseQuery).toString(),
    deserializeQuerystring,
    includedFieldIds: BROWSE_SEARCH_PAGE_FACET_FIELD_IDS,
    serializeQuerystring
  });

  const backToLinkProps = makeBackToLinkProps(intl.formatMessage, {
    backToCmsPath,
    backToCmsTitle,
    currentLibrary
  });

  const appliedFiltersAccessibilityHeading = 'TODO: Accessibility Heading';
  const facetsHeading = intl.formatMessage({
    defaultMessage: 'Browse facets',
    id: 'browse_facets'
  });
  const heading = overrideHeading || intl.formatMessage({ id: 'browse' });

  if (!facetFields) {
    return null;
  }
  return (
    <>
      <DocumentHeadContainer pageTitle={heading} />
      <BrowseSearchPage backToLinkProps={backToLinkProps} heading={heading}>
        <FilterableDisplayBibGrid
          appliedFiltersAccessibilityHeading={appliedFiltersAccessibilityHeading}
          displayBibs={displayBibs}
          facetFields={facetFields}
          facetsHeading={facetsHeading}
          handleFilterChange={handleFilterChange}
          hasBibRatings
          isFetching={isFetching}
          pagination={pagination}
          setShowMoreFieldId={setShowMoreFieldId}
          unapplyAllFilters={unapplyAllFilters}
        />
      </BrowseSearchPage>
    </>
  );
};

export default BrowseSearchPageContainer;
